import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';

// Custom validator per conferma password
function passwordMatchValidator(form: FormGroup) {
  const password = form.get('password');
  const confirmPassword = form.get('confirmPassword');
  
  if (password && confirmPassword && password.value !== confirmPassword.value) {
    return { passwordMismatch: true };
  }
  return null;
}

@Component({
  selector: 'app-recovery-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, RouterModule],
  templateUrl: './recovery-password.component.html',
  styleUrls: ['./recovery-password.component.css']
})
export class RecoveryPasswordComponent implements OnInit {
  passwordForm: FormGroup;
  isSubmitted: boolean = false;
  isLoading: boolean = false;
  passwordReset: boolean = false;
  token: string | null = null;
  tokenValid: boolean = false;
  tokenExpired: boolean = false;
  generalError: string = '';
  showGeneralError: boolean = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private translate: TranslateService
  ) {
    this.passwordForm = this.fb.group({
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(50),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/)
      ]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: passwordMatchValidator });
  }

  ngOnInit(): void {
    // Estrai il token dai parametri della query
    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
      if (this.token) {
        this.validateToken();
      } else {
        this.handleInvalidAccess();
      }
    });
  }

  private async validateToken(): Promise<void> {
    if (!this.token) {
      this.handleInvalidAccess();
      return;
    }

    try {
      const isValid = await this.authService.validatePasswordResetToken(this.token);
      this.tokenValid = isValid;
      
      if (!isValid) {
        this.tokenExpired = true;
        this.showError('RECOVERY_PASSWORD.TOKEN_EXPIRED');
      }
    } catch (error) {
      console.error('Error validating token:', error);
      this.tokenExpired = true;
      this.showError('RECOVERY_PASSWORD.TOKEN_INVALID');
    }
  }

  private handleInvalidAccess(): void {
    this.showError('RECOVERY_PASSWORD.INVALID_ACCESS');
    // Reindirizza alla pagina di recovery dopo 3 secondi
    setTimeout(() => {
      this.router.navigate(['/recovery']);
    }, 3000);
  }

  onSubmit(): void {
    this.isSubmitted = true;
    this.showGeneralError = false;    if (this.passwordForm.valid && this.tokenValid && this.token) {
      this.isLoading = true;
      const password = this.passwordForm.value.password;
      const confirmPassword = this.passwordForm.value.confirmPassword;

      this.authService.resetPassword(this.token, password, confirmPassword)
        .then((success) => {
          this.isLoading = false;
          if (success) {
            this.passwordReset = true;
            // Reindirizza al login dopo 3 secondi
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000);
          } else {
            this.showError('RECOVERY_PASSWORD.RESET_FAILED');
          }
        })
        .catch((error: Error) => {
          this.isLoading = false;
          console.error('Error resetting password:', error);
          this.showError(error.message || 'RECOVERY_PASSWORD.RESET_FAILED');
        });
    }
  }

  private showError(messageKey: string): void {
    this.generalError = messageKey;
    this.showGeneralError = true;
  }

  /**
   * Requisiti della password come lista da completare: ogni voce si spunta
   * mentre l'utente digita. Rispecchia i validator del form.
   */
  get passwordRequirements(): { labelKey: string; met: boolean }[] {
    const password = this.passwordForm.get('password')?.value || '';
    const confirmPassword = this.passwordForm.get('confirmPassword')?.value || '';

    return [
      { labelKey: 'RECOVERY_PASSWORD.REQ_LENGTH', met: password.length >= 8 && password.length <= 50 },
      { labelKey: 'RECOVERY_PASSWORD.REQ_LOWERCASE', met: /[a-z]/.test(password) },
      { labelKey: 'RECOVERY_PASSWORD.REQ_UPPERCASE', met: /[A-Z]/.test(password) },
      { labelKey: 'RECOVERY_PASSWORD.REQ_DIGIT', met: /\d/.test(password) },
      { labelKey: 'RECOVERY_PASSWORD.REQ_SPECIAL', met: /[@$!%*?&]/.test(password) },
      { labelKey: 'RECOVERY_PASSWORD.REQ_MATCH', met: !!password && password === confirmPassword }
    ];
  }

  /** Numero di requisiti soddisfatti, per l'indicatore di avanzamento */
  get metRequirementsCount(): number {
    return this.passwordRequirements.filter(requirement => requirement.met).length;
  }

  getErrorMessage(field: string): string {
    const control = this.passwordForm.get(field);
    
    if (control?.hasError('required')) {
      return `RECOVERY_PASSWORD.${field.toUpperCase()}_REQUIRED`;
    }
    
    if (field === 'password') {
      if (control?.hasError('minlength')) {
        return 'RECOVERY_PASSWORD.PASSWORD_MIN_LENGTH';
      }
      if (control?.hasError('maxlength')) {
        return 'RECOVERY_PASSWORD.PASSWORD_MAX_LENGTH';
      }
      if (control?.hasError('pattern')) {
        return 'RECOVERY_PASSWORD.PASSWORD_PATTERN';
      }
    }
    
    return '';
  }

  getFormError(): string {
    if (this.passwordForm.hasError('passwordMismatch')) {
      return 'RECOVERY_PASSWORD.PASSWORD_MISMATCH';
    }
    return '';
  }

  hasError(field: string): boolean {
    const control = this.passwordForm.get(field);
    return !!(this.isSubmitted && control?.invalid);
  }

  hasFormError(): boolean {
    return this.isSubmitted && this.passwordForm.hasError('passwordMismatch');
  }

  hasGeneralError(): boolean {
    return this.showGeneralError || this.tokenExpired;
  }

  getGeneralError(): string {
    if (this.tokenExpired) {
      return 'RECOVERY_PASSWORD.TOKEN_EXPIRED';
    }
    return this.generalError;
  }
}
